import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, Route, Routes, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { Tarif } from '../../billtoro/types';
import { fehlendeFunktion, planName, upgradeAnbieten } from '../../billtoro/upgrade';
import { Dialog } from '../../components/Dialog';
import { fmtDate } from '../../lib/format';
import { ChipRadios } from '../ChipRadios';
import { darf, GesperrtKnopf, MitRecht, NurAnsehen } from '../tarifHinweis';
import type { Overview } from '../types';
import { urlId } from '../urlId';
import { BuchenDialog } from './BuchenDialog';
import { LagerArtikel } from './LagerArtikel';
import { artikelText, bestandCsvUrl, bewegungenCsvUrl, LAGER_SATZ, mengeText, passtArtikel, useLager, type Lager as LagerDaten } from './LagerDaten';
import { Zaehlen } from './Zaehlen';
import './lager.css';

// Lager (nur Billtoro): /rechnung/lager (Liste mit Bestand, ?filter=wenig), /zaehlen, /:id (Verlauf eines Artikels).
// Ansehen darf jeder Login der Firma; Buchen und Zählen mit dem Recht „Kontakte, Leistungen und Lager“ und nur mit
// Lager im Tarif (sonst öffnen die Knöpfe „Mehr mit Billtoro“), die Listen als CSV mit „Exporte“ in jedem Tarif. Was
// erlaubt ist, entscheidet der Server. ?zugang=<Ausgabe> öffnet den Zugang mit den Positionen der Ausgabe (Ausgaben,
// „Ins Lager übernehmen“).
export function Lager({ data }: { data: Overview }) {
  const lager = useLager(data.firm.id);
  return (
    <Routes>
      <Route index element={<Liste data={data} lager={lager} />} />
      <Route
        path="zaehlen"
        element={
          <MitRecht tarif={data.tarif} bereich="kunden" zu="/rechnung/lager">
            <Zaehlen data={data} lager={lager} />
          </MitRecht>
        }
      />
      <Route path=":id" element={<Verlauf data={data} lager={lager} />} />
      <Route path="*" element={<Navigate to="/rechnung/lager" replace />} />
    </Routes>
  );
}

// Artikel aus der Adresse geht in API-Pfade: nur mit echter ID (urlId.ts)
function Verlauf({ data, lager }: { data: Overview; lager: LagerDaten }) {
  const id = urlId(useParams().id);
  return id ? <LagerArtikel key={id} data={data} id={id} lager={lager} /> : <Navigate to="/rechnung/lager" replace />;
}

type Filter = 'alle' | 'wenig';
const FILTER: [Filter, string][] = [
  ['alle', 'Alle'],
  ['wenig', 'Wenig Bestand'],
];

function Liste({ data, lager }: { data: Overview; lager: LagerDaten }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filter: Filter = params.get('filter') === 'wenig' ? 'wenig' : 'alle';
  const [q, setQ] = useState('');
  const [buchen, setBuchen] = useState<'zugang' | 'abgang' | null>(null);
  const [exportieren, setExportieren] = useState(false);
  const tarif = data.tarif;
  const pflegen = darf(tarif, 'kunden');
  const exportRecht = darf(tarif, 'export');
  const fehlt = fehlendeFunktion(tarif, 'lager');
  const { liste, fehler } = lager;
  const zugangVon = urlId(params.get('zugang'));
  const [ausgabeId, setAusgabeId] = useState<string | null>(null);
  // kam mit ?zugang= (für den Satz bei leerer Liste; der Parameter ist dann schon weg)
  const vonAusgabe = useRef(false);
  // aus den Ausgaben („Ins Lager übernehmen“): Zugang mit den Positionen der Ausgabe, einmal je Aufruf
  useEffect(() => {
    if (!zugangVon || !liste) return;
    vonAusgabe.current = true;
    const p = new URLSearchParams(params);
    p.delete('zugang');
    setParams(p, { replace: true });
    if (!pflegen || !liste.artikel.length) return; // nur ansehen bzw. leere Liste (Hinweis unten)
    if (fehlt) {
      upgradeAnbieten(tarif, 'lager');
      return;
    }
    setAusgabeId(zugangVon);
    setBuchen('zugang');
  }, [zugangVon, liste]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFilter = (f: Filter) => {
    const p = new URLSearchParams(params);
    if (f === 'wenig') p.set('filter', 'wenig');
    else p.delete('filter');
    setParams(p, { replace: true });
  };

  // Knöpfe zum Buchen: ohne Lager im Tarif mit Marke, öffnet „Mehr mit Billtoro“
  const knopf = (text: string, an: () => void, primary = false) =>
    fehlt ? (
      <GesperrtKnopf tarif={tarif} fehlt={fehlt} className={`btn${primary ? ' primary' : ''}`} onUpgrade={() => upgradeAnbieten(tarif, 'lager')}>
        {text}
      </GesperrtKnopf>
    ) : (
      <button type="button" className={`btn${primary ? ' primary' : ''}`} onClick={an}>
        {text}
      </button>
    );

  const alle = liste?.artikel ?? [];
  const shown = alle.filter((a) => (filter === 'alle' || a.wenig) && passtArtikel(a, q));

  return (
    <div className="lg">
      <div className="rt-head">
        <div>
          <h1>
            Lager
            <NurAnsehen tarif={tarif} bereich="kunden" />
          </h1>
          <p className="muted">{liste ? (alle.length ? `${artikelText(alle.length)} mit Bestand` : 'Bestand je Artikel') : ' '}</p>
        </div>
        {alle.length > 0 && (pflegen || exportRecht) && (
          <div className="rt-head-act">
            {/* am Handy als Leiste unten */}
            {pflegen && (
              <div className="lg-knoepfe lg-leiste">
                {knopf('Zugang', () => setBuchen('zugang'), true)}
                {knopf('Abgang', () => setBuchen('abgang'))}
                {knopf('Zählen', () => navigate('/rechnung/lager/zaehlen'))}
              </div>
            )}
            {exportRecht && (
              <button type="button" className="btn" onClick={() => setExportieren(true)}>
                Exportieren
              </button>
            )}
          </div>
        )}
      </div>
      <p className="lg-satz muted">{LAGER_SATZ}</p>
      <RueckstufungHinweis tarif={tarif} da={alle.length > 0} />

      {!liste ? (
        fehler ? <div className="notice bad">{fehler}</div> : <div className="spinner" aria-label="Lädt" />
      ) : alle.length === 0 ? (
        <div className="rt-card rt-empty">
          <p>
            Noch kein Artikel mit Bestand. Setzen Sie unter Leistungen und Artikel den Haken „Bestand führen“.
            {vonAusgabe.current && ' Danach können Sie die Ausgabe ins Lager übernehmen.'}
          </p>
          <Link to="/rechnung/leistungen" className="btn">
            Zu Leistungen und Artikel
          </Link>
        </div>
      ) : (
        <>
          <div className="rt-tools">
            <input
              type="search"
              className="rt-search"
              value={q}
              placeholder="Suchen: Name oder Artikelnummer"
              aria-label="Lager durchsuchen"
              onChange={(e) => setQ(e.target.value)}
            />
            <ChipRadios<Filter> label="Filter" value={filter} options={FILTER} onChange={setFilter} />
          </div>
          {shown.length === 0 ? (
            <div className="rt-card rt-empty">
              <p>{filter === 'wenig' && !q ? 'Kein Artikel mit wenig Bestand.' : 'Kein Artikel passt zu dieser Auswahl.'}</p>
              <button
                type="button"
                className="btn small"
                onClick={() => {
                  setQ('');
                  setFilter('alle');
                }}
              >
                Alle zeigen
              </button>
            </div>
          ) : (
            <>
              <div className="lg-lk" aria-hidden="true">
                <span>Artikelnummer</span>
                <span>Artikel</span>
                <span className="r">Bestand</span>
                <span className="r">Mindestbestand</span>
                <span>Letzte Bewegung</span>
              </div>
              <ul className="lg-liste" aria-label="Artikel im Lager">
                {shown.map((a) => (
                  <li key={a.id}>
                    <Link to={`/rechnung/lager/${a.id}`} className="lg-lz">
                      <span className="lg-lz-nr num">{a.artikelnummer ?? ''}</span>
                      <b className="lg-lz-name">{a.name}</b>
                      <span className={`lg-lz-b num r${a.bestand < 0 ? ' bad' : a.wenig ? ' warn' : ''}`}>{mengeText(a.bestand, a.unit)}</span>
                      <span className="lg-lz-min num r">
                        {a.mindest !== null && (
                          <>
                            <span className="lg-nur-handy">Mindestbestand </span>
                            {mengeText(a.mindest, a.unit)}
                          </>
                        )}
                      </span>
                      <span className="lg-lz-letzte muted">
                        {a.letzte && (
                          <>
                            <span className="lg-nur-handy">Letzte Bewegung </span>
                            {fmtDate(a.letzte)}
                          </>
                        )}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}

      {liste && (
        <BuchenDialog
          open={!!buchen}
          art={buchen ?? 'zugang'}
          firmId={data.firm.id}
          heute={data.today}
          artikel={liste.artikel}
          ausgabeId={buchen === 'zugang' ? ausgabeId : null}
          onClose={() => {
            setBuchen(null);
            setAusgabeId(null);
          }}
          onGebucht={() => void lager.laden()}
        />
      )}
      <ExportFenster open={exportieren} data={data} onClose={() => setExportieren(false)} />
    </div>
  );
}

// Nach einer Rückstufung: Bestand, Verlauf und Export bleiben, Belege buchen weiter ab; von Hand buchen erst wieder mit Lager
function RueckstufungHinweis({ tarif, da }: { tarif: Tarif | null | undefined; da: boolean }) {
  const fehlt = fehlendeFunktion(tarif, 'lager');
  if (!tarif || !da || !fehlt || fehlt === 'bald') return null;
  return (
    <p className="notice warn">
      Zugang, Abgang und Zählen gibt es wieder im Tarif {planName(tarif, fehlt)}. Rechnungen und Lieferscheine buchen den Bestand weiter ab.
    </p>
  );
}

// Export: Bestand zum Stichtag oder Bewegungen im Zeitraum als CSV (in jedem Tarif)
function ExportFenster({ open, data, onClose }: { open: boolean; data: Overview; onClose(): void }) {
  const heute = data.today;
  const jahr = Number(heute.slice(0, 4));
  const vorjahrEnde = `${jahr - 1}-12-31`;
  const [stichtag, setStichtag] = useState(heute);
  const [von, setVon] = useState(`${jahr}-01-01`);
  const [bis, setBis] = useState(heute);
  const firmId = data.firm.id;
  const zeitraumOk = !!von && !!bis && von <= bis;
  return (
    <Dialog open={open} onClose={onClose} title="Lager exportieren" className="rt-exp-dlg">
      <section className="lg-exp">
        <h3>Bestand zum Stichtag</h3>
        <div className="lg-exp-zeile">
          <label className="field">
            <span>Stichtag</span>
            <input type="date" value={stichtag} max={heute} onChange={(e) => setStichtag(e.target.value)} />
          </label>
          <button type="button" className="rt-chip" onClick={() => setStichtag(vorjahrEnde)}>
            31.12.{jahr - 1}
          </button>
        </div>
        {stichtag ? (
          <a className="btn primary" href={bestandCsvUrl(firmId, stichtag)} download>
            Bestand als CSV
          </a>
        ) : (
          <button type="button" className="btn primary" disabled>
            Bestand als CSV
          </button>
        )}
      </section>
      <section className="lg-exp">
        <h3>Bewegungen im Zeitraum</h3>
        <div className="lg-exp-zeile">
          <label className="field">
            <span>von</span>
            <input type="date" value={von} max={heute} onChange={(e) => setVon(e.target.value)} />
          </label>
          <label className="field">
            <span>bis</span>
            <input type="date" value={bis} max={heute} onChange={(e) => setBis(e.target.value)} />
          </label>
        </div>
        {zeitraumOk ? (
          <a className="btn primary" href={bewegungenCsvUrl(firmId, von, bis)} download>
            Bewegungen als CSV
          </a>
        ) : (
          <button type="button" className="btn primary" disabled>
            Bewegungen als CSV
          </button>
        )}
      </section>
      <div className="dlg-actions">
        <button type="button" className="btn" onClick={onClose}>
          Schließen
        </button>
      </div>
    </Dialog>
  );
}
